import { __assign } from 'tslib';
import { colors } from '../colors.js';
import { buttons } from './buttons.js';

var selectButton = {
    base: __assign(__assign({}, buttons.base), { margin: 0, color: "text", backgroundColor: "white", borderColor: "black.20", borderStyle: "solid", borderWidth: 1, borderRadius: 3, lineHeight: 4, fontWeight: "body", width: "100%", cursor: "pointer", "&[data-is-open=true][data-direction=up]": {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
        }, "&[data-is-open=true][data-direction=down]": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        }, "&[data-disabled]": {
            opacity: "inherit",
            cursor: "inherit",
            boxShadow: "inherit",
            backgroundColor: "black.5",
        } }),
    variants: {
        invalid: {
            true: {
                borderColor: "critical.60",
            },
        },
        size: {
            sm: {
                fontSize: 2,
                "--button-height": "33px",
                paddingX: "8px",
            },
            md: {
                "--button-height": "44px",
                paddingX: "16px",
            },
            lg: {
                "--button-height": "56px",
                paddingX: "16px",
            },
        },
    },
};
var selectableOption = {
    base: {
        fontFamily: "body",
        cursor: "pointer",
        "&[data-active=true]": {
            backgroundColor: "primary.5",
            color: "primary.100",
        },
        "&[aria-disabled=true]": {
            backgroundColor: "black.5",
            color: "black.100",
            cursor: "default",
        },
    },
    variants: {
        highlightSelected: {
            true: {
                "&[aria-selected=true]": {
                    border: "2px solid #cce0f5",
                },
            },
        },
        look: {
            float: {
                borderRadius: 0,
            },
            dropdown: {
                borderRadius: 0,
            },
        },
        size: {
            sm: {
                fontSize: 2,
                padding: "8px 17px",
            },
            md: {
                padding: "12px 17px",
            },
            lg: {
                padding: "18px 25px",
            },
        },
    },
    defaultProps: {
        size: "md",
    },
};
var selectDropdown = {
    base: {
        "[data-dropdown]": {
            border: "1px solid",
            borderColor: "black.20",
            overflow: "hidden",
        },
        "&[data-direction=up] [data-dropdown]": {
            borderTopLeftRadius: 3,
            borderTopRightRadius: 3,
            borderBottom: 0,
        },
        "&[data-direction=down] [data-dropdown]": {
            borderBottomLeftRadius: 3,
            borderBottomRightRadius: 3,
            borderTop: 0,
        },
        "[data-dropdown] > *": {
            border: 0,
            borderBottom: "1px",
            borderColor: "black.20",
            borderStyle: "solid none",
        },
        "[data-dropdown] > *:last-child": {
            borderBottom: 0,
        },
    },
    variants: {
        type: {
            button: {
                width: "300px",
                marginTop: "4px",
                backgroundColor: "white",
                "&[data-direction=down] [data-dropdown]": {
                    borderRadius: 3,
                    borderTop: "1px solid",
                    borderColor: "black.20",
                },
                "&[data-direction=up] [data-dropdown]": {
                    borderRadius: 3,
                    borderBottom: "1px solid",
                    borderColor: "black.20",
                },
            },
        },
    },
    variantOrder: ["type"],
    defaultProps: {
        type: "",
    },
};
var selectableOptions = {
    base: {
        margin: 0,
        background: "white",
        listStyle: "none",
        marginBlockStart: 0,
        marginBlockEnd: 0,
        marginInlineStart: 0,
        marginInlineEnd: 0,
        paddingInlineStart: 0,
        ":focus": {
            outline: "none",
        },
    },
    variants: {
        keyboard: {
            true: {
                ":focus": {
                    boxShadow: "0 0 0 4px ".concat(colors.primary["20"]),
                },
            },
        },
        look: {
            float: {
                border: "1px solid",
                borderColor: "black.20",
                "li:first-of-type": {
                    borderTopLeftRadius: 3,
                    borderTopRightRadius: 3,
                },
                "li:last-child": {
                    borderBottomLeftRadius: 3,
                    borderBottomRightRadius: 3,
                },
            },
        },
    },
};
var selectOption = {
    base: {
        display: "grid",
        gridTemplateColumns: "min-content 1fr min-content",
        alignItems: "center",
        justifyItems: "start",
        width: "100%",
        "[data-icon]": {
            color: "black.60",
            display: "flex",
            alignItems: "center",
        },
        "[data-decor]": { color: "black.60" },
    },
};
var selectSection = {
    base: {
        bg: "white",
        py: 3,
        px: 5,
        font: "body",
    },
    variants: {
        look: {
            description: {
                font: "xs",
                color: "black.60",
            },
            heading: {
                textAlign: "center",
                color: "black.100",
                fontSize: "md",
                fontWeight: "bold",
                py: "20px",
                px: 5,
            },
            input: {
                bg: "black.3",
                p: "9px",
            },
            backButton: {
                lineHeight: 1,
                font: "xs",
                p: 0,
            },
        },
    },
    variantOrder: ["look"],
    defaultProps: {
        look: "description",
    },
};
var selectBackButton = {
    base: __assign(__assign({}, buttons.base), { textAlign: "left", "--button-color-solid": "var(--colors-primary-100)", font: "sm" }),
    variants: {
        look: {
            link: {
                backgroundColor: "transparent",
                border: "none",
                color: "var(--button-color-solid)",
                display: "inline-block",
                lineHeight: "inherit",
                height: "auto",
                width: "100%",
                padding: "0",
                ":hover:not(:disabled):not([data-disabled='true'])": {
                    border: "none",
                    boxShadow: "none",
                    textDecoration: "underline",
                },
                ":focus:not(:disabled):not([data-disabled='true'])": {
                    border: "none",
                    outline: "none",
                    borderColor: "var(--button-color-solid)",
                    textDecoration: "underline",
                },
                ":disabled, &[data-disabled='true']": {
                    textDecoration: "none",
                },
            },
        },
        size: {
            sm: {
                height: "33px",
                paddingX: "16px",
            },
            md: {
                height: "44px",
                paddingX: "16px",
            },
            lg: {
                height: "56px",
                paddingX: "16px",
            },
        },
    },
    variantOrder: ["look", "size"],
    defaultProps: {
        look: "link",
        size: "md",
    },
};

export { selectBackButton, selectButton, selectDropdown, selectOption, selectSection, selectableOption, selectableOptions };
//# sourceMappingURL=select.js.map
